import React, { useState, useEffect } from 'react';
import { Layout, Menu, Dropdown, Avatar, Space, Button } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  TeamOutlined,
  ShoppingOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  HeartOutlined,
  GiftOutlined,
  PictureOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BookOutlined,
  CheckCircleOutlined,
  SwapOutlined,
  DollarOutlined,
  AccountBookOutlined,
  StarOutlined,
  FolderOutlined,
  BankOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { getUser, clearAuth, isAuthenticated } from '../utils/storage';
import type { User } from '../types/models';

const { Header, Sider, Content } = Layout;

const LayoutComponent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }
    const u = getUser();
    if (u) setUser(u);
  }, []);

  const menuItems = [
    { key: '/dashboard', icon: <DashboardOutlined />, label: '仪表盘' },
    {
      key: 'student-group',
      icon: <UserOutlined />,
      label: '学员管理',
      children: [
        { key: '/students', label: '学员列表' },
        { key: '/student-balances', label: '课时余额' },
      ]
    },
    { key: '/teachers', icon: <TeamOutlined />, label: '教师管理' },
    { key: '/lesson-packages', icon: <BookOutlined />, label: '课时包' },
    {
      key: 'lesson-group',
      icon: <ClockCircleOutlined />,
      label: '消课排课',
      children: [
        { key: '/consumptions', label: '消课记录' },
        { key: '/schedules', label: '排课管理' },
        { key: '/attendance', label: '考勤管理' },
      ]
    },
    {
      key: 'class-course-group',
      icon: <FolderOutlined />,
      label: '课程班级',
      children: [
        { key: '/courses', label: '课程管理' },
        { key: '/classes', label: '班级管理' },
      ]
    },
    { key: '/customers', icon: <HeartOutlined />, label: '客户跟踪' },
    { key: '/artworks', icon: <PictureOutlined />, label: '学员作品' },
    { key: '/growth', icon: <BankOutlined />, label: '成长档案' },
    { key: '/teacher-evaluation', icon: <StarOutlined />, label: '教师评估' },
    {
      key: 'finance-group',
      icon: <DollarOutlined />,
      label: '财务管理',
      children: [
        { key: '/payments', label: '缴费订单' },
        { key: '/refunds', label: '退转课' },
        { key: '/salary', label: '教师工资' },
        { key: '/finance', label: '财务报表' },
      ]
    },
    { key: '/coupons', icon: <GiftOutlined />, label: '优惠券' },
    { key: '/settings', icon: <SettingOutlined />, label: '系统设置' },
  ];

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  const userMenuItems = [
    { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', onClick: handleLogout },
  ];

  return (
    <Layout style={{ height: '100vh' }}>
      <Sider
        width={220}
        theme="dark"
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        breakpoint="lg"
        style={{ background: 'var(--sidebar-bg)' }}
      >
        <div style={{
          height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.08)', margin: '0 16px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: collapsed ? 0 : 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 8,
              background: 'rgba(255,255,255,0.10)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
            }}>
              <BookOutlined style={{ color: '#fff', fontSize: 18 }} />
            </div>
            {!collapsed && (
              <span style={{ color: '#fff', fontSize: 17, fontWeight: 700, whiteSpace: 'nowrap', letterSpacing: '-0.3px' }}>
                智慧教育
              </span>
            )}
          </div>
        </div>

        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          defaultOpenKeys={['student-group', 'lesson-group', 'class-course-group', 'finance-group']}
          items={menuItems}
          onClick={({ key }) => { if (key.startsWith('/')) navigate(key); }}
          style={{ background: 'transparent', borderRight: 'none' }}
        />
      </Sider>

      <Layout>
        <Header style={{
          background: '#FFFFFF',
          padding: '0 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          boxShadow: 'none', position: 'sticky', top: 0, zIndex: 10,
          borderBottom: '1px solid var(--border-color)',
        }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ fontSize: 16, width: 40, height: 40, color: 'var(--text-secondary)' }}
          />
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Space style={{ cursor: 'pointer' }}>
              <Avatar size="small" icon={<UserOutlined />} style={{ background: 'var(--primary-color)' }} />
              <span style={{ fontSize: 14, color: 'var(--text-primary)', fontWeight: 600 }}>
                {user?.name || user?.username || '管理员'}
              </span>
            </Space>
          </Dropdown>
        </Header>

        <Content style={{
          margin: '16px', padding: '20px', background: '#F8F9FA',
          borderRadius: '12px', overflow: 'auto', minHeight: 0,
        }}>
          <div style={{
            background: 'var(--bg-card)',
            padding: '24px', borderRadius: '12px', minHeight: '100%',
            border: '1px solid var(--border-light)', boxShadow: 'var(--shadow-sm)',
          }}>
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default LayoutComponent;
